import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import cx from 'classnames';
import styled from 'styled-components';
import isEmpty from 'lodash/isEmpty';
import Button from '@narvar/nth-button';
import s from './ReturnInstructions.module.css';
import AdditionalDetails from '../AdditionalDetails';
import { StoreLogo } from '../StoreLogo/StoreLogo';
import PickupDate from '../PickupsPage/PickupDate';
import { timeWindowFormatter } from '../../lib/dates';
import { formatAddressMultiLine } from '../../lib/addresses';
import { titan } from '../../lib/productAnalytics';
import { TITAN_EVENTS, TITAN_EXPERIMENT_IDS } from '../../constants/titan';

const mapStateToProps = ({
  page: { countryCode } = {},
  language,
  retailerSettings: { branding: { brand_call_to_action: brandCTAColor } = {} } = {},
  returnSubmission: { multiLabel: { packagesCount } = {} } = {},
  storeLocator: { selectedLocation: { brand_display_name: brandDisplayName } = {} } = {},
}) => ({
  language,
  brandCTAColor,
  packagesCount,
  brandDisplayName,
  countryCode,
});

const propTypes = {
  language: PropTypes.object.isRequired,
  methodKey: PropTypes.string.isRequired,
  number: PropTypes.number,
  button: PropTypes.bool,
  location: PropTypes.object,
  storeLink: PropTypes.bool,
  onClick: PropTypes.func,
  displayBullet: PropTypes.bool,
  partnerReturnCode: PropTypes.string,
  brandCTAColor: PropTypes.string,
  returnMethod: PropTypes.string,
  pickupDate: PropTypes.string,
  pickupTime: PropTypes.string,
  packagesCount: PropTypes.number,
  brandDisplayName: PropTypes.string,
  countryCode: PropTypes.string.isRequired,
};

const defaultProps = {
  number: null,
  button: false,
  location: {},
  storeLink: false,
  onClick: null,
  displayBullet: true,
  partnerReturnCode: null,
  brandCTAColor: '#000000',
  pickupDate: '',
  pickupTime: '',
  returnMethod: null,
  packagesCount: null,
  brandDisplayName: null,
};

const StoreInfoWrapper = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  grid-gap: 10px;
`;

const SelectedLocationText = styled.p`
  margin: 0;
  padding: 0;
`;

const Address = styled.address`
  margin-left: 1rem;
  margin-bottom: 0;
`;

const StoreAddressInfo = ({
  logos,
  company,
  address1,
  address2,
  city,
  region,
  postalCode,
  countryCode,
  businessInfo,
  dropOffHeader,
  number,
}) => (
  <React.Fragment>
    {dropOffHeader && (
      <div
        className={cx(`return-instructions-${number}-header`, s.instruction)}
        dangerouslySetInnerHTML={{ __html: dropOffHeader }}
      />
    )}
    <StoreInfoWrapper>
      {logos && logos.square_2x && logos.square_2x.url ? (
        <StoreLogo logo={logos.square_2x.url} alt={company} />
      ) : null}
      <Address>
        {formatAddressMultiLine({
          address: {
            company,
            address1,
            address2,
            city,
            region,
            postalCode,
          },
          countryCode,
        }).map((item) => (
          <SelectedLocationText dangerouslySetInnerHTML={{ __html: item }} />
        ))}
      </Address>
    </StoreInfoWrapper>
    <AdditionalDetails items={businessInfo} />
  </React.Fragment>
);

StoreAddressInfo.defaultProps = {
  company: undefined,
  address1: undefined,
  address2: undefined,
  city: undefined,
  region: undefined,
  postalCode: undefined,
};

StoreAddressInfo.propTypes = {
  logos: PropTypes.shape({
    square_2x: PropTypes.shape({
      url: PropTypes.string,
    }),
  }).isRequired,
  company: PropTypes.string,
  address1: PropTypes.string,
  address2: PropTypes.string,
  city: PropTypes.string,
  region: PropTypes.string,
  postalCode: PropTypes.string,
  countryCode: PropTypes.string.isRequired,
  phoneNumber: PropTypes.string.isRequired,
  businessInfo: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      value: PropTypes.string,
    }),
  ).isRequired,
  dropOffHeader: PropTypes.string.isRequired,
  number: PropTypes.string.isRequired,
};

const generateLoopUrl = ({ currentOrderNumber }) => {
  const { origin, pathname, search } = window.location;
  const [retailerMoniker, application] = pathname.split('/').filter(Boolean);
  const urlPath = [retailerMoniker, application].join('/');
  const params = new URLSearchParams(search);
  const orderPath = decodeURIComponent(params.get('path') || '')
    .split(',')
    .filter(Boolean);
  orderPath.push(currentOrderNumber);
  params.set('path', orderPath.join(','));
  return {
    href: `${origin}/${urlPath}/?${params.toString()}`,
    orderPath,
  };
};

const ReturnLoop = ({ description, linkText, currentOrderNumber }) => {
  const { href, orderPath } = generateLoopUrl({ currentOrderNumber });

  useEffect(() => {
    titan.event(TITAN_EVENTS.EXPERIMENT_VIEWED, {
      experimentId: TITAN_EXPERIMENT_IDS.RETURNS_MULTIORDER_LOOP_LINK,
      order_id: currentOrderNumber,
      total: orderPath.length,
      checkout_id: orderPath.join(','),
      extra_properties: [
        { key: 'version', value: '1' },
        { key: 'orderPath', value: orderPath.join(',') },
        { key: 'event', value: 'view-new-order-link' },
      ],
    });
  }, []);

  const onClick = () => {
    titan.event(TITAN_EVENTS.EXPERIMENT_VIEWED, {
      experimentId: TITAN_EXPERIMENT_IDS.RETURNS_MULTIORDER_LOOP_LINK,
      order_id: currentOrderNumber,
      total: orderPath.length,
      checkout_id: orderPath.join(','),
      extra_properties: [
        { key: 'version', value: '1' },
        { key: 'orderPath', value: orderPath.join(',') },
        { key: 'event', value: 'click-new-order-link' },
      ],
    });
    return true;
  };

  return (
    <div style={{ marginTop: '14px' }}>
      <span style={{ marginRight: '8px' }}>{description}</span>
      <a href={href} target="_blank" rel="noopener noreferrer" onClick={onClick}>
        {linkText}
      </a>
    </div>
  );
};

ReturnLoop.propTypes = {
  description: PropTypes.string.isRequired,
  linkText: PropTypes.string.isRequired,
  currentOrderNumber: PropTypes.number.isRequired,
};

const Instruction = ({
  language,
  number,
  button,
  location,
  storeLink,
  methodKey,
  onClick,
  displayBullet,
  partnerReturnCode,
  brandCTAColor,
  pickupDate,
  pickupTime,
  packagesCount,
  brandDisplayName,
  countryCode: currentCountryCode,
}) => {
  const hasLocation = !isEmpty(location) && !isEmpty(location.store_address);
  const {
    store_name: company,
    store_address: {
      address_line1: address1,
      address_line2: address2,
      city,
      region_code: region,
      postal_code: postalCode,
      country_code: countryCode,
    } = {},
    additional_details: { business_info: businessInfo = [] } = {},
    logos,
  } = location;
  const {
    [`returns.instruction[${number - 1}].dropoff_location.${methodKey}`]: dropOffHeader,
    [`returns.instruction[${number - 1}].${methodKey}`]: header,
    [`returns.instruction[${number - 1}].${methodKey}.multilabel`]: multiLabelHeader,
    [`returns.instruction[${number - 1}].description_${methodKey}`]: description,
    [`returns.instruction[${number - 1}].time_window_${methodKey}`]: descriptionTimeWindow,
    [`returns.instruction[${number - 1}].button_${methodKey}`]: buttonText,
    [`returns.instruction[${number - 1}].button_${methodKey}.multilabel`]: multiLabelButtonText,
    [`returns.instruction[1].locator_link_${methodKey}`]: storeLinkURL,
    [`returns.instruction[1].locator_link_text_${methodKey}`]: storeLinkText,
  } = language;

  const parsePickupTimeString = (time) => {
    const [from, to] = timeWindowFormatter(time, currentCountryCode).split('-');
    return descriptionTimeWindow.replace('{from}', from).replace('{to}', to);
  };

  let instructionHeader =
    packagesCount && multiLabelHeader
      ? multiLabelHeader.replace('{labels}', packagesCount)
      : header;
  let instructionDescription = description;

  if (brandDisplayName) {
    instructionHeader = instructionHeader
      ? instructionHeader.replace('$brand', brandDisplayName)
      : instructionHeader;
    instructionDescription = instructionDescription
      ? instructionDescription.replace('$brand', brandDisplayName)
      : instructionDescription;
  }

  return (
    <li className={`return-instructions-${number}-container`}>
      {displayBullet ? (
        <div className={cx(s.list_number, 'return-instructions-num-container')}>
          {<div className={`return-instructions-${number}-num`}>{number}</div>}
        </div>
      ) : (
        <div className={s.list_number} style={{ border: 'none' }} />
      )}

      <div className={s.instruction_content}>
        {hasLocation ? (
          <React.Fragment>
            <StoreAddressInfo
              {...{
                logos,
                company,
                address1,
                address2,
                city,
                region,
                postalCode,
                countryCode,
                businessInfo,
                dropOffHeader,
                number,
              }}
            />
          </React.Fragment>
        ) : (
          <React.Fragment>
            <div
              className={cx(`return-instructions-${number}-header`, s.instruction)}
              dangerouslySetInnerHTML={{
                __html: instructionHeader,
              }}
              role="heading"
              aria-level="2"
            />
            {partnerReturnCode && (
              <div
                style={{ marginTop: '5px', fontSize: '1.25rem', color: brandCTAColor }}
                className={cx(`return-instructions-${number}-partner-code`)}
              >
                {partnerReturnCode}
              </div>
            )}
            {pickupDate && <PickupDate size="medium" date={pickupDate} />}
            {pickupTime && descriptionTimeWindow ? (
              parsePickupTimeString(pickupTime)
            ) : (
              <div
                style={{ marginTop: '5px' }}
                className={`return-instructions-${number}-description`}
                dangerouslySetInnerHTML={{ __html: instructionDescription }}
              />
            )}
          </React.Fragment>
        )}
        {storeLink && (
          <div>
            <a
              className={`return-instructions-${number}-store-link`}
              href={storeLinkURL}
              rel="noopener noreferrer"
              target="_blank"
            >
              {storeLinkText}
            </a>
          </div>
        )}
        {button && (
          <div className={s.print_button_container}>
            <div className={cx(s.print_button, 'print-label-button')}>
              <Button
                className={cx('return-button', 'btn', 'narvar-btn-primary', 'return-button-text')}
                s={{ fontSize: '14px' }}
                onClick={onClick}
              >
                {packagesCount && multiLabelButtonText ? multiLabelButtonText : buttonText}
              </Button>
            </div>
          </div>
        )}
      </div>
      <div className="clearfix" />
    </li>
  );
};

Instruction.propTypes = propTypes;
Instruction.defaultProps = defaultProps;

const InstructionList = ({ children }) => (
  <div className={s.container}>
    <div>
      <ol className={s.list}>{children}</ol>
    </div>
  </div>
);
InstructionList.propTypes = { children: PropTypes.node };
InstructionList.defaultProps = { children: null };

export default connect(mapStateToProps)(Instruction);
export { InstructionList, Instruction };
